/**
 * Session card component displaying session info
 */
import { Link } from 'react-router-dom';
import { Play, Clock, Lock, Heart, Sparkles } from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  SESSION_IMAGE_GRADIENT,
  SessionImageFallback,
} from '@/components/ui';
import { useLanguage, useIsAuthenticated, useUser, usePlaySession } from '@/store';
import { useToggleFavorite } from '@/hooks';
import { cn } from '@/utils/cn';
import { getCategoryLabel } from '@/utils/categories';
import { formatSessionDuration } from '@/utils/sessionBrowse';
import { getSessionCardTranslation } from '@/utils';
import type { Session, LocalizedSession, SessionDisplay } from '@/types';
import { ROUTES } from '@/config/routes';
import { hasAccess } from '@/utils/access';

interface SessionCardProps {
  session: Session | LocalizedSession | SessionDisplay;
  isFavorite?: boolean;
  onGuidedSession?: (session: Session | LocalizedSession | SessionDisplay) => void;
}

export function SessionCard({ session, isFavorite = false, onGuidedSession }: SessionCardProps) {
  const language = useLanguage();
  const isAuthenticated = useIsAuthenticated();
  const user = useUser();
  const playSession = usePlaySession();
  const toggleFavorite = useToggleFavorite();

  // Session data is already localized by the hooks - use title/description directly
  const title = session.title;
  const description = session.description;
  const categoryLabel = getCategoryLabel(session.category, language);

  // Every plan and the trial open premium sessions (R2, R5).
  const canAccess = !session.is_premium || hasAccess(user);
  const showLock = !canAccess;

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isAuthenticated) {
      toggleFavorite.mutate(session.id);
    }
  };

  const handlePlayClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (canAccess) {
      playSession(session);
    }
  };

  const handleGuidedClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (canAccess && onGuidedSession) {
      onGuidedSession(session);
    }
  };

  return (
    <Link to={ROUTES.sessionDetail(session.id)}>
      <Card className="h-full transition-all hover:shadow-lg hover:scale-[1.02] group">
        {/* Image placeholder or gradient */}
        <div className={cn('relative h-32 sm:h-40 rounded-t-xl overflow-hidden', SESSION_IMAGE_GRADIENT)}>
          {session.image_url ? (
            <img
              src={session.image_url}
              alt={title}
              className="w-full h-full object-cover"
            />
          ) : (
            <SessionImageFallback className="absolute inset-0" iconClassName="h-12 w-12" />
          )}

          {/* Premium badge */}
          {session.is_premium && (
            <div className="absolute top-2 left-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-medium px-2 py-1 rounded-full">
              {getSessionCardTranslation('premium', language)}
            </div>
          )}

          {/* Lock overlay for premium sessions */}
          {showLock && (
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
              <Lock className="h-8 w-8 text-white" />
            </div>
          )}

          {/* Favorite button */}
          {isAuthenticated && (
            <button
              onClick={handleFavoriteClick}
              className={cn(
                'absolute top-2 right-2 p-2 rounded-full transition-colors',
                isFavorite
                  ? 'bg-red-500 text-white'
                  : 'bg-white/80 text-gray-600 hover:bg-white hover:text-red-500'
              )}
              disabled={toggleFavorite.isPending}
            >
              <Heart className={cn('h-4 w-4', isFavorite && 'fill-current')} />
            </button>
          )}
        </div>

        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-primary uppercase tracking-wide">
              {categoryLabel}
            </span>
            <span className="flex items-center text-xs text-muted-foreground">
              <Clock className="h-3 w-3 mr-1" />
              {formatSessionDuration(session.duration)}
            </span>
          </div>
          <CardTitle className="text-lg line-clamp-2">{title}</CardTitle>
        </CardHeader>

        <CardContent>
          {description && (
            <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
              {description}
            </p>
          )}

          {/* Action buttons */}
          <div className="flex gap-2">
            {/* Play button */}
            <Button
              variant={canAccess ? 'default' : 'outline'}
              size="sm"
              className="flex-1"
              disabled={!canAccess}
              onClick={handlePlayClick}
            >
              {showLock ? (
                <>
                  <Lock className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">{getSessionCardTranslation('upgrade', language)}</span>
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">{getSessionCardTranslation('play', language)}</span>
                </>
              )}
            </Button>

            {/* Guided Session button - golden style like old frontend */}
            {canAccess && onGuidedSession && (
              <button
                onClick={handleGuidedClick}
                className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all"
                title={getSessionCardTranslation('guidedSession', language)}
              >
                <Sparkles className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">{getSessionCardTranslation('guided', language)}</span>
              </button>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
